/**
 * Kaunis Slider Component
 * A minimal, dependency-free Web Component for modern browsers.
 */

if (!customElements.get('kaunis-slider')) {
  class KaunisSlider extends HTMLElement {
    constructor() {
      super();
      this.scrollHandler = this.updateButtons.bind(this);
      this.resizeHandler = this.debounce(() => {
        this.initDots();
        this.updateButtons();
      }, 200);
      
      // Drag state
      this.isDown = false;
      this.startX = 0;
      this._sliderBlockScroll = false;
    }

    connectedCallback() {
      this.viewport = this.querySelector('.kaunis-slider__viewport');
      this.track = this.querySelector('.kaunis-slider__track');
      this.prevBtn = this.querySelector('.kaunis-prev');
      this.nextBtn = this.querySelector('.kaunis-next');
      this.dotsContainer = this.querySelector('.kaunis-dots--progress') || this.querySelector('.kaunis-dots--circles');

      if (this.viewport) {
        this.viewport.addEventListener('scroll', this.scrollHandler, { passive: true });
        window.addEventListener('resize', this.resizeHandler);
        
        // Mouse drag events - Matching theme's "swipe to trigger" behavior
        this.viewport.addEventListener('mousedown', (e) => this.dragStart(e));
        window.addEventListener('mousemove', (e) => this.dragMove(e));
        window.addEventListener('mouseup', (e) => this.dragEnd(e));
        
        if (this.dotsContainer) {
          this.initDots();
        }
        
        setTimeout(() => this.updateButtons(), 100);
      }

      this.nextBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        this.step('next');
      });

      this.prevBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        this.step('prev');
      });
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.scrollHandler);
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('mousemove', (e) => this.dragMove(e));
      window.removeEventListener('mouseup', (e) => this.dragEnd(e));
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    dragStart(e) {
      if (e.button !== 0) return; // Left click only
      this.isDown = true;
      this.startX = e.screenX;
      this.viewport.style.cursor = 'grabbing';
    }

    dragEnd(e) {
      if (!this.isDown) return;
      this.isDown = false;
      this.viewport.style.cursor = 'grab';
      this.viewport.classList.remove('pointer-events-off');
    }

    dragMove(e) {
      if (!this.isDown) return;
      
      let directionX = this.startX - e.screenX;
      
      // テーマと同じく、10px以上の移動でスライドを切り替える
      if (Math.abs(directionX) > 10) {
        this.viewport.classList.add('pointer-events-off'); // ドラッグ中のクリック無効化
        
        if (directionX > 0) {
          this.step('next');
        } else {
          this.step('prev');
        }
        
        // 1回トリガーしたらドラッグ終了（テーマの挙動）
        this.isDown = false;
        this.viewport.style.cursor = 'grab';
        
        // 少し遅れてクリック制限を解除
        setTimeout(() => {
          this.viewport.classList.remove('pointer-events-off');
        }, 100);
      }
    }

    initDots() {
      if (!this.dotsContainer) return;
      const items = this.querySelectorAll('.kaunis-slider__item');
      const visibleItems = Math.floor(parseFloat(getComputedStyle(this).getPropertyValue('--kaunis-visible-items')) || 1);
      
      const dotCount = Math.ceil(items.length / visibleItems);
      
      this.dotsContainer.style.setProperty('--size', dotCount);
      this.dotsContainer.innerHTML = '';
      
      for (let i = 0; i < dotCount; i++) {
        const dot = document.createElement('span');
        dot.classList.add('kaunis-dot');
        if (i === 0) dot.classList.add('active');
        this.dotsContainer.appendChild(dot);
      }
    }

    step(direction) {
      if (!this.viewport || this._sliderBlockScroll) return;
      
      const items = this.querySelectorAll('.kaunis-slider__item');
      const visibleItems = Math.floor(parseFloat(getComputedStyle(this).getPropertyValue('--kaunis-visible-items')) || 1);
      const itemWidth = items[0]?.offsetWidth + (parseInt(getComputedStyle(this).getPropertyValue('--kaunis-gap')) || 0);
      const scrollStep = itemWidth * visibleItems;

      // 連続スクロールを防止（テーマの挙動）
      this._sliderBlockScroll = true;
      setTimeout(() => {
        this._sliderBlockScroll = false;
      }, 400);

      this.viewport.scrollBy({
        left: direction === 'next' ? scrollStep : -scrollStep,
        behavior: 'smooth'
      });
    }

    updateButtons() {
      if (!this.viewport) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
      const maxScrollLeft = scrollWidth - clientWidth;
      
      if (this.prevBtn) this.prevBtn.disabled = scrollLeft <= 5;
      if (this.nextBtn) this.nextBtn.disabled = scrollLeft >= maxScrollLeft - 5;

      if (this.dotsContainer) {
        const items = this.querySelectorAll('.kaunis-slider__item');
        const visibleItems = Math.floor(parseFloat(getComputedStyle(this).getPropertyValue('--kaunis-visible-items')) || 1);
        const itemWidth = items[0]?.offsetWidth + (parseInt(getComputedStyle(this).getPropertyValue('--kaunis-gap')) || 0);
        
        let currentIndex;
        if (scrollLeft >= maxScrollLeft - 5) {
          currentIndex = this.dotsContainer.querySelectorAll('.kaunis-dot').length - 1;
        } else {
          currentIndex = Math.round(scrollLeft / (itemWidth * visibleItems));
        }
        
        const dots = this.dotsContainer.querySelectorAll('.kaunis-dot');
        dots.forEach((dot, i) => {
          dot.classList.toggle('active', i <= currentIndex);
        });
      }
    }
  }

  customElements.define('kaunis-slider', KaunisSlider);
}
